import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { flip, landed } from './actions';
import { selectTossing } from './selectors';
import { Coin, CoinSpot, Coins, Heads, Hint, Label, Overlay, Spinner, Tails, Tray } from './styled';

/** How long one coin is in the air, the pause between coins, and how long the results stay before the move shows. */
const TOSS_MS = 1100;
const BETWEEN_MS = 150;
const SETTLE_MS = 900;
/** How high a coin goes and how many times it turns over on the way. */
const HEIGHT_PX = 140;
const TURNS = 4;
/** Steps the toss is drawn in; enough that the face switches when the coin is edge-on. */
const FRAMES = 60;

/**
 * One coin up and down, turning over TURNS times and a half more for tails. It's drawn in 2D: the turn squashes it
 * (scaleY is the cosine of the angle) and the face showing switches each time it's edge-on.
 */
function toss(spinner, heads) {
  const end = (TURNS * 2 + (heads ? 0 : 1)) * Math.PI;
  const frames = [];
  const facing = [];
  for (let step = 0; step <= FRAMES; step++) {
    const t = step / FRAMES;
    // Fast up, slowing at the top, falling back: a parabola; the turning eases out as it lands.
    const height = 4 * t * (1 - t) * HEIGHT_PX;
    const angle = end * (1 - (1 - t) * (1 - t));
    const turn = Math.cos(angle);
    frames.push({ offset: t, transform: `translateY(${-height}px) scaleY(${Math.max(Math.abs(turn), 0.04)})` });
    facing.push({ offset: t, opacity: turn >= 0 ? 1 : 0 });
  }
  const timing = { duration: TOSS_MS, fill: 'forwards' };
  spinner.animate(frames, timing);
  spinner.firstChild.animate(facing, timing);
}

/**
 * A move's coin flips tossed on the board before the move shows. Your own wait for you to tap the coin; the
 * opponent's toss by themselves. The result was decided with the move; tapping is what shows it. Coins land one after
 * another, each saying heads or tails, then the move goes ahead (landed). Nothing spins for people who ask their
 * device for less motion: the results just show.
 */
const CoinToss = () => {
  const dispatch = useDispatch();
  const tossing = useSelector(selectTossing);
  const coins = useRef([]);
  const [shown, setShown] = useState(0);

  const tossVersion = tossing && tossing.version;
  useEffect(() => {
    setShown(0);
  }, [tossVersion]);

  useEffect(() => {
    if (!tossing || !tossing.flipping) {
      return undefined;
    }
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let cancelled = false;
    const timers = [];
    const later = (ms, then) => timers.push(setTimeout(() => !cancelled && then(), ms));

    const tossOne = (i) => {
      if (i >= tossing.flips.length) {
        later(SETTLE_MS, () => dispatch(landed()));
        return;
      }
      const coin = coins.current[i];
      if (!reduced && coin) {
        toss(coin, tossing.flips[i].heads);
      } else if (coin) {
        coin.firstChild.style.opacity = tossing.flips[i].heads ? '1' : '0';
      }
      later(reduced ? 0 : TOSS_MS, () => {
        setShown(i + 1);
        later(BETWEEN_MS, () => tossOne(i + 1));
      });
    };
    tossOne(0);

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [tossing, dispatch]);

  if (!tossing) {
    return null;
  }

  const waiting = !tossing.flipping;
  // A move can flip for more than one thing (Confusion, then the attack's own coins): name each once.
  const label = [...new Set(tossing.flips.map(result => result.label))].join(' · ');

  return (
    <Overlay>
      <Tray role="dialog" aria-label={`Coin flip: ${label}`}>
        <Label>{label}</Label>
        <Coins>
          {tossing.flips.map((result, i) => (
            <CoinSpot key={i}>
              <Coin
                $waiting={waiting}
                disabled={!waiting}
                onClick={() => dispatch(flip())}
                aria-label={waiting ? 'Flip the coin' : undefined}
              >
                <Spinner ref={(element) => { coins.current[i] = element; }}>
                  <Heads />
                  <Tails />
                </Spinner>
              </Coin>
              <span aria-live="polite">{i < shown ? (result.heads ? 'Heads' : 'Tails') : ' '}</span>
            </CoinSpot>
          ))}
        </Coins>
        <Hint>{waiting ? `Tap the coin${tossing.flips.length > 1 ? 's' : ''} to flip` : ' '}</Hint>
      </Tray>
    </Overlay>
  );
};

export default CoinToss;
